import { downloadEncryptedMediaBlob, markEncryptedMediaCached } from '../api/mediaE2EE';
import { getOrCreateWebDeviceId } from '../device/webDeviceId';
import { getRecipientKeys } from '../crypto/recipientKeyCache';
import { unwrapContentKeyForRecipient } from '../crypto/recipientKeyUnwrap';
import { decryptMediaBase64WithContentKey } from './mediaCrypto';
import { getMediaManifest, isRestrictedOnceMediaItem, mediaMime } from './mediaMessageUtils';
import {
  deleteCachedEncryptedMediaBlob,
  getCachedEncryptedMediaBlob,
  putCachedEncryptedMediaBlob,
} from './webMediaBlobCache';

function bytesToBase64(bytes: Uint8Array) {
  let s = '';

  for (let i = 0; i < bytes.length; i += 1) {
    s += String.fromCharCode(bytes[i]);
  }

  return btoa(s);
}

async function arrayBufferToBase64(data: ArrayBuffer | Uint8Array | Blob | any): Promise<string> {
  if (typeof data === 'string') return data;

  if (data?.data) return arrayBufferToBase64(data.data);
  if (data?.body) return arrayBufferToBase64(data.body);
  if (data?.payload) return arrayBufferToBase64(data.payload);
  if (data?.blob) return arrayBufferToBase64(data.blob);

  if (data?.arrayBuffer && typeof data.arrayBuffer === 'function') {
    const buf = await data.arrayBuffer();
    return bytesToBase64(new Uint8Array(buf));
  }

  if (data instanceof Uint8Array) return bytesToBase64(data);
  if (data instanceof ArrayBuffer) return bytesToBase64(new Uint8Array(data));

  if (data instanceof Blob) {
    const buf = await data.arrayBuffer();
    return bytesToBase64(new Uint8Array(buf));
  }

  if (Array.isArray(data)) return bytesToBase64(new Uint8Array(data));
  if (data?.data && Array.isArray(data.data)) return bytesToBase64(new Uint8Array(data.data));

  throw new Error(`invalid_media_blob_response:${Object.prototype.toString.call(data)}`);
}

function base64ToBlob(base64: string, mimeType: string) {
  const bin = atob(String(base64 || ''));
  const bytes = new Uint8Array(bin.length);

  for (let i = 0; i < bin.length; i += 1) {
    bytes[i] = bin.charCodeAt(i);
  }

  return new Blob([bytes], { type: mimeType || 'application/octet-stream' });
}

export function webMediaIdFromItem(item: any) {
  const manifest = getMediaManifest(item || {});

  return String(
    item?.mediaId ||
    item?.media_id ||
    item?.mediaMessageId ||
    item?.media_message_id ||
    item?.id ||
    item?.clientMessageId ||
    item?.client_message_id ||
    manifest?.mediaId ||
    manifest?.media_id ||
    manifest?.id ||
    ''
  ).replace(/^media:/, '').trim();
}

function allCandidateEnvelopes(manifest: any, currentUserId: string) {
  const out: any[] = [];
  const seen = new Set<string>();

  function add(env: any, source: string) {
    if (!env) return;

    const wrappedKeyBase64 = String(env?.wrappedKeyBase64 || '').trim();
    const senderKeyId = String(env?.senderKeyId || '').trim();

    if (!wrappedKeyBase64 || !senderKeyId) return;

    const recipientUserId = String(env?.recipientUserId || currentUserId || '').trim();
    const key = `${source}:${recipientUserId}:${senderKeyId}:${wrappedKeyBase64}`;

    if (seen.has(key)) return;
    seen.add(key);

    out.push({
      ...env,
      recipientUserId,
      __source: source,
    });
  }

  const envelopes = Array.isArray(manifest?.encryption?.recipientEnvelopes)
    ? manifest.encryption.recipientEnvelopes
    : [];

  for (const env of envelopes) {
    if (String(env?.recipientUserId || '') === String(currentUserId)) {
      add(env, 'current-user');
    }
  }

  // Development/backward compatibility for older manifests.
  for (const env of envelopes) {
    add(env, 'fallback-any-recipient');
  }

  add(manifest?.encryption?.wrappedContentKey, 'legacy-wrappedContentKey');

  return out;
}

async function decryptWithAnyEnvelope(args: {
  manifest: any;
  currentUserId: string;
  ciphertextBase64: string;
}) {
  const candidates = allCandidateEnvelopes(args.manifest, args.currentUserId);
  const tried: any[] = [];

  if (!candidates.length) throw new Error('missing_recipient_envelope');

  for (const env of candidates) {
    const senderKeyId = String(env?.senderKeyId || '').trim();
    const wrappedKeyBase64 = String(env?.wrappedKeyBase64 || '').trim();
    const recipientUserId = String(env?.recipientUserId || args.currentUserId || '').trim();

    if (!senderKeyId || !wrappedKeyBase64 || !recipientUserId) continue;

    try {
      const keys = await getRecipientKeys([recipientUserId]);
      const key = keys[0];

      if (!key?.publicKeyBase64) {
        tried.push({ source: env.__source, recipientUserId, error: 'missing_public_key' });
        continue;
      }

      const contentKeyBase64 = unwrapContentKeyForRecipient({
        recipientPublicKeyBase64: key.publicKeyBase64,
        senderKeyId,
        wrappedKeyBase64,
      });

      return decryptMediaBase64WithContentKey({
        ciphertextBase64: args.ciphertextBase64,
        ivBase64: String(
          args.manifest?.encryption?.iv ||
          args.manifest?.encryption?.ivBase64 ||
          args.manifest?.ivBase64 ||
          args.manifest?.iv ||
          ''
        ),
        contentKeyBase64,
      });
    } catch (e: any) {
      tried.push({
        source: env.__source,
        recipientUserId,
        error: e?.message || String(e),
      });
    }
  }

  console.warn('[web-media-decrypt] candidates failed', tried);
  throw new Error('unwrap_failed');
}

async function getCiphertextBase64ForMedia(args: {
  mediaId: string;
  item: any;
  mimeType: string;
}): Promise<string> {
  const mediaId = String(args.mediaId || '').trim();
  const restrictedOnce = isRestrictedOnceMediaItem(args.item);

  if (restrictedOnce) {
    await deleteCachedEncryptedMediaBlob(mediaId).catch(() => {});
  }

  if (!restrictedOnce) {
    const cached = await getCachedEncryptedMediaBlob(mediaId);
    if (cached?.ciphertextBase64) {
      return cached.ciphertextBase64;
    }
  }

  let encrypted: any;

  try {
    encrypted = await downloadEncryptedMediaBlob(mediaId);
  } catch (e: any) {
    const cached = !restrictedOnce ? await getCachedEncryptedMediaBlob(mediaId) : null;

    if (cached?.ciphertextBase64) {
      return cached.ciphertextBase64;
    }

    throw new Error(`download_failed:${e?.message || String(e)}`);
  }

  let ciphertextBase64 = '';

  try {
    ciphertextBase64 = await arrayBufferToBase64(encrypted as any);
  } catch (e: any) {
    throw new Error(`download_decode_failed:${e?.message || String(e)}`);
  }

  if (!restrictedOnce && ciphertextBase64) {
    const cached = await putCachedEncryptedMediaBlob({
      mediaId,
      ciphertextBase64,
      mimeType: args.mimeType,
    }).catch(() => false);

    if (cached) {
      await markEncryptedMediaCached(mediaId, {
        deviceId: getOrCreateWebDeviceId(),
        client: 'web',
        storageKind: 'indexeddb',
      }).catch((e: any) => {
        console.warn('[web-media-decrypt] cached ack failed', {
          mediaId,
          message: e?.message || String(e),
        });
      });
    }
  }

  return ciphertextBase64;
}

export async function ensureWebMediaEncryptedBlobCached(args: {
  item: any;
  mediaId?: string;
  mimeType?: string;
}) {
  const item = args.item || {};
  const mediaId = String(args.mediaId || webMediaIdFromItem(item) || '').replace(/^media:/, '').trim();

  if (!mediaId) return false;
  if (isRestrictedOnceMediaItem(item)) return false;

  const manifest = getMediaManifest(item);
  const mimeType =
    String(args.mimeType || '').trim() ||
    mediaMime(item) ||
    String(manifest?.mimeType || manifest?.mime_type || 'application/octet-stream');

  const ciphertextBase64 = await getCiphertextBase64ForMedia({
    mediaId,
    item,
    mimeType,
  });

  return !!ciphertextBase64;
}

export async function decryptWebMediaToObjectUrl(args: {
  item: any;
  currentUserId: string;
}) {
  const item = args.item || {};
  const mediaId = webMediaIdFromItem(item);

  if (!mediaId) throw new Error('missing_media_id');

  const currentUserId = String(args.currentUserId || '').trim();
  if (!currentUserId) throw new Error('missing_current_user_id');

  const manifest = getMediaManifest(item);
  const mimeType = mediaMime(item) || String(manifest?.mimeType || manifest?.mime_type || 'application/octet-stream');

  const ivBase64 = String(
    manifest?.encryption?.iv ||
    manifest?.encryption?.ivBase64 ||
    manifest?.ivBase64 ||
    manifest?.iv ||
    item?.ivBase64 ||
    item?.iv ||
    ''
  ).trim();

  if (!ivBase64) throw new Error(`missing_media_iv:${mediaId}`);

  const ciphertextBase64 = await getCiphertextBase64ForMedia({
    mediaId,
    item,
    mimeType,
  });

  const plainBase64 = await decryptWithAnyEnvelope({
    manifest,
    currentUserId,
    ciphertextBase64,
  });

  const blob = base64ToBlob(plainBase64, mimeType);
  const objectUrl = URL.createObjectURL(blob);

  return {
    mediaId,
    mimeType,
    blob,
    objectUrl,
  };
}
